import { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CaretLeft, CaretRight, ArrowCounterClockwise, ArrowClockwise } from '@phosphor-icons/react';
import { FilePdf, FilmSlate, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { isTranscoding, isFailed } from '@/utils/mediaStatus';
import RangeSlider from '../RangeSlider/RangeSlider';
import './ImageGallery.styles.css';

const ImageGallery = ({ 
  images, 
  initialIndex = 0, 
  maxZoom = 8, 
  onZoomedInteractionStart, 
  onZoomedInteractionEnd,
  showNavButtons = true,
  onIndexChange,
  onImageClick,
  renderCaption,
  rotation,
  onRotateLeft,
  onRotateRight,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Compute the current item and whether it's a PDF so we can switch rendering/behavior
  const safeIndex = Math.max(0, Math.min(selectedIndex, Math.max(0, images.length - 1)));
  const currentItem = images[safeIndex];
  const isPdf = Boolean(currentItem?.type === 'pdf' || /\.pdf(?:$|\?)/i.test(currentItem?.url || ''));
  const isVideo = Boolean(currentItem?.type === 'video' || /\.(mp4|mov|webm)(?:$|\?)/i.test(currentItem?.url || ''));
  // Video still transcoding (no usable url yet) or that failed to transcode.
  const isItemTranscoding = isTranscoding(currentItem);
  const isItemFailed = isFailed(currentItem);
  const rotationDeg = Number.isFinite(rotation) ? rotation : 0;

  // Reset position and scale when image changes
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [selectedIndex, rotationDeg]);

  // Notify parent on index initialization
  useEffect(() => {
    if (typeof onIndexChange === 'function') {
      onIndexChange(initialIndex);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyIndexChange = useCallback((nextIdx) => {
    setSelectedIndex(nextIdx);
    if (typeof onIndexChange === 'function') {
      onIndexChange(nextIdx);
    }
  }, [onIndexChange]);

  const getBoundedPosition = useCallback((newX, newY, currentScale) => {
    if (!imageRef.current || !containerRef.current) return { x: 0, y: 0 };

    const image = imageRef.current;
    const container = containerRef.current;
    
    // Calculate boundaries
    const scaledWidth = image.offsetWidth * currentScale;
    const scaledHeight = image.offsetHeight * currentScale;
    
    const maxX = Math.max(0, (scaledWidth - container.offsetWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - container.offsetHeight) / 2);
    
    // Bound the position within the limits
    return {
      x: Math.min(maxX, Math.max(-maxX, newX)),
      y: Math.min(maxY, Math.max(-maxY, newY))
    };
  }, []);

  const handleZoomChange = useCallback((newScale, dx = 0, dy = 0) => {
    if (newScale === 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      
      // Notify parent that we're no longer zoomed
      if (onZoomedInteractionEnd) onZoomedInteractionEnd();
      return;
    }

    if (newScale !== scale) {
      const deltaX = (newScale - scale) * (dx / scale);
      const deltaY = (newScale - scale) * (dy / scale);
      const newPosition = getBoundedPosition(
        position.x - deltaX,
        position.y - deltaY,
        newScale
      );

      setScale(newScale);
      setPosition(newPosition);
      
      // If zooming in, notify parent
      if (newScale > 1 && scale === 1 && onZoomedInteractionStart) {
        onZoomedInteractionStart();
      }
    }
  }, [scale, position, getBoundedPosition, onZoomedInteractionStart, onZoomedInteractionEnd]);

  // Add non-passive wheel event listener (skip for PDFs)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isPdf || isVideo) return;

    const handleWheel = (e) => {
      e.preventDefault();
      if (!imageRef.current || !containerRef.current) return;

      const rect = container.getBoundingClientRect();

      // Get mouse position relative to container
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const dx = mouseX - centerX;
      const dy = mouseY - centerY;

      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(Math.max(1, scale * zoomFactor), maxZoom);

      handleZoomChange(newScale, dx, dy);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [scale, maxZoom, handleZoomChange, isPdf, isVideo]);

  // Mouse events
  const handleMouseDown = useCallback((e) => {
    if (isPdf || isVideo) return;
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      
      // Notify parent component that we started dragging a zoomed image
      if (onZoomedInteractionStart) onZoomedInteractionStart();
      
      // Stop propagation to prevent the overlay from handling this event
      e.stopPropagation();
    }
  }, [scale, position, onZoomedInteractionStart, isPdf, isVideo]);

  const handleMouseMove = useCallback((e) => {
    if (isPdf || isVideo) return;
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      const boundedPosition = getBoundedPosition(newX, newY, scale);
      setPosition(boundedPosition);
      
      // Stop propagation to prevent the overlay from handling this event
      e.stopPropagation();
    }
  }, [isDragging, dragStart, scale, getBoundedPosition, isPdf, isVideo]);

  const handleMouseUp = useCallback((e) => {
    if (isPdf || isVideo) return;
    if (isDragging) {
      setIsDragging(false);
      
      // Notify parent component that we stopped dragging
      if (onZoomedInteractionEnd) onZoomedInteractionEnd();
      
      // Stop propagation to prevent the overlay from handling this event
      if (e) e.stopPropagation();
    }
  }, [isDragging, onZoomedInteractionEnd, isPdf, isVideo]);

  // Touch events for panning and zooming
  const lastTouchDistance = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });

  const handleTouchStart = useCallback((e) => {
    if (isPdf || isVideo) return;
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartRef.current = { 
        x: touch.clientX, 
        y: touch.clientY, 
        time: Date.now() 
      };
      
      if (scale > 1) {
        setIsDragging(true);
        setDragStart({ 
          x: touch.clientX - position.x, 
          y: touch.clientY - position.y 
        });
        
        // Notify parent component that we started zoomed interaction
        if (onZoomedInteractionStart) onZoomedInteractionStart();
        
        // Stop propagation
        e.stopPropagation();
      } else {
        setIsSwiping(true);
      }
    } else if (e.touches.length === 2) {
      // Cancel any swiping or dragging when pinch-zooming
      setIsSwiping(false);
      setIsDragging(false);
      
      // Notify parent component that we started zoomed interaction
      if (onZoomedInteractionStart) onZoomedInteractionStart();
      
      // Calculate initial distance between two fingers
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      lastTouchDistance.current = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      
      // Stop propagation
      e.stopPropagation();
    }
  }, [scale, position, onZoomedInteractionStart, isPdf, isVideo]);

  const handleTouchMove = useCallback((e) => {
    if (isPdf || isVideo) return;
    if (e.touches.length === 1 && isDragging) {
      // Single touch for panning when zoomed in
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      const boundedPosition = getBoundedPosition(newX, newY, scale);
      setPosition(boundedPosition);
      
      // Prevent default to stop page scrolling when panning
      e.preventDefault();
      e.stopPropagation();
    } else if (e.touches.length === 2) {
      // Two touches for pinch-zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // Calculate new distance
      const currentDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      
      if (lastTouchDistance.current) {
        // Calculate scale factor
        const scaleFactor = currentDistance / lastTouchDistance.current;
        const newScale = Math.min(Math.max(1, scale * scaleFactor), maxZoom);
        
        // Get center point between two fingers
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        // Get container bounding rect
        const rect = containerRef.current.getBoundingClientRect();
        
        // Calculate dx and dy relative to center of container
        const dx = centerX - (rect.left + rect.width / 2);
        const dy = centerY - (rect.top + rect.height / 2);
        
        // Update scale and position
        handleZoomChange(newScale, dx, dy);
        
        // Prevent default to stop page zooming
        e.preventDefault();
        e.stopPropagation();
      }
      
      lastTouchDistance.current = currentDistance;
    }
  }, [isDragging, dragStart, scale, maxZoom, getBoundedPosition, handleZoomChange, isPdf, isVideo]);

  const handleTouchEnd = useCallback((e) => {
    if (isPdf || isVideo) return;
    const wasZoomed = scale > 1;
    lastTouchDistance.current = null;
    
    if (isDragging) {
      setIsDragging(false);
      
      // Notify parent that interaction ended
      if (onZoomedInteractionEnd) onZoomedInteractionEnd();
      
      // If was zoomed, stop propagation
      if (wasZoomed) {
        e.stopPropagation();
      }
    }
    
    // Handle swipe navigation when not zoomed
    if (isSwiping && scale <= 1 && e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaTime = Date.now() - touchStartRef.current.time;
      
      // Only process quick swipes (less than 300ms)
      if (Math.abs(deltaX) > 50 && deltaTime < 300) {
        if (deltaX > 0 && selectedIndex > 0) {
          // Swipe right to previous
          notifyIndexChange(selectedIndex - 1);
        } else if (deltaX < 0 && selectedIndex < images.length - 1) {
          // Swipe left to next
          notifyIndexChange(selectedIndex + 1);
        }
      }
    }
    
    setIsSwiping(false);
  }, [isSwiping, scale, selectedIndex, images.length, isDragging, onZoomedInteractionEnd, notifyIndexChange, isPdf, isVideo]);

  // Add touch event listeners with proper passive settings (skip for PDFs)
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isPdf || isVideo) return;
    
    // Use non-passive listener for touchmove to allow preventing default
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isPdf, isVideo]);

  const handleThumbnailClick = (index) => {
    notifyIndexChange(index);
  };

  // Add navigation handlers
  const handleNext = () => {
    notifyIndexChange((selectedIndex + 1) % images.length);
  };

  const handlePrevious = () => {
    notifyIndexChange((selectedIndex - 1 + images.length) % images.length);
  };

  // Double tap to zoom
  const lastTapTimeRef = useRef(0);
  
  const handleDoubleTap = useCallback((e) => {
    if (isPdf || isVideo) return;

    const target = e.target;
    if (target?.closest?.('.gallery-controls') || target?.closest?.('.gallery-nav-button') || target?.closest?.('.thumbnail-button')) {
      lastTapTimeRef.current = 0;
      return;
    }
    const now = Date.now();
    const timeDiff = now - lastTapTimeRef.current;
    
    if (timeDiff < 300) {
      // This is a double tap
      if (scale > 1) {
        // If already zoomed, reset to 1x
        setScale(1);
        setPosition({ x: 0, y: 0 });
        
        // Notify parent that we're no longer zoomed
        if (onZoomedInteractionEnd) onZoomedInteractionEnd();
      } else {
        // Zoom to 2.5x at the tap position
        const newScale = 2.5;
        
        // Calculate center point for zoom
        const rect = containerRef.current.getBoundingClientRect();
        let touchX, touchY;
        
        if (e.type === 'touchend') {
          // For touch events
          const touch = e.changedTouches[0];
          touchX = touch.clientX;
          touchY = touch.clientY;
        } else {
          // For mouse events
          touchX = e.clientX;
          touchY = e.clientY;
        }
        
        const dx = touchX - (rect.left + rect.width / 2);
        const dy = touchY - (rect.top + rect.height / 2);
        
        handleZoomChange(newScale, dx, dy);
        
        // Notify parent that we're zoomed in
        if (onZoomedInteractionStart) onZoomedInteractionStart();
      }
      
      // Prevent the next single tap
      e.preventDefault();
      e.stopPropagation();
      lastTapTimeRef.current = 0;
    } else {
      // This is a single tap, store the time
      lastTapTimeRef.current = now;
    }
  }, [scale, handleZoomChange, onZoomedInteractionStart, onZoomedInteractionEnd, isPdf, isVideo]);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('click', handleDoubleTap);
    container.addEventListener('touchend', handleDoubleTap, { passive: false });
    
    return () => {
      container.removeEventListener('click', handleDoubleTap);
      container.removeEventListener('touchend', handleDoubleTap);
    };
  }, [handleDoubleTap]);

  // Ensure cleanup of zoomed state on unmount
  useEffect(() => {
    return () => {
      if (scale > 1 && onZoomedInteractionEnd) {
        onZoomedInteractionEnd();
      }
    };
  }, [scale, onZoomedInteractionEnd]);

  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < images.length - 1;

  return (
    <div 
      className="image-gallery"
      onClick={e => scale > 1 && e.stopPropagation()}
    >
      <div className="main-image-wrapper">
        {/* Only show navigation buttons if showNavButtons is true */}
        {showNavButtons && (
          <button 
            className="gallery-nav-button prev" 
            onClick={handlePrevious} 
            disabled={!canGoPrevious}
            aria-label="Previous image"
          >
            <CaretLeft size={32} weight="bold" />
          </button>
        )}
        <div 
          ref={containerRef}
          className={`main-image-container ${scale > 1 ? 'zoomed' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {currentItem && (
            isItemTranscoding ? (
              <div
                className="main-image"
                style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#6b7280' }}
              >
                <SpinnerGap size={40} className="animate-spin" />
                <span style={{ fontSize: 14 }}>Se procesează…</span>
              </div>
            ) : isItemFailed ? (
              <div
                className="main-image"
                style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#9ca3af' }}
              >
                <WarningCircle size={40} weight="duotone" />
                <span style={{ fontSize: 14 }}>Procesare video eșuată</span>
              </div>
            ) : isPdf ? (
              <iframe
                src={currentItem.url}
                title={currentItem.alt}
                className="pdf-viewer"
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            ) : isVideo ? (
              <video
                src={currentItem.url}
                className="main-image"
                style={{ width: '100%', height: '100%' }}
                controls
                playsInline
              />
            ) : (
              <img
                ref={imageRef}
                src={currentItem.url}
                alt={currentItem.alt}
                className="main-image"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotationDeg}deg)`,
                }}
                draggable={false}
                role={onImageClick ? 'button' : undefined}
                tabIndex={onImageClick ? 0 : undefined}
                onClick={onImageClick ? (event) => {
                  event.stopPropagation();
                  if (scale === 1) onImageClick(currentItem, safeIndex);
                } : undefined}
                onKeyDown={onImageClick ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onImageClick(currentItem, safeIndex);
                  }
                } : undefined}
              />
            )
          )}
          {typeof renderCaption === 'function' && (
            <div className={`absolute ${isPdf ? 'left-3 bottom-3 sm:left-4 sm:bottom-4' : 'left-3 top-3 sm:left-4 sm:top-4'} z-10 pointer-events-none`}>
              {renderCaption({ image: images[selectedIndex], index: selectedIndex })}
            </div>
          )}
          {!isPdf && !isVideo && (
            <div className="gallery-controls">
              {typeof onRotateLeft === 'function' && (
                <button
                  type="button"
                  className="gallery-rotate-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotateLeft();
                  }}
                  aria-label="Rotate image counterclockwise"
                >
                  <ArrowCounterClockwise size={18} weight="bold" />
                </button>
              )}
              <RangeSlider
                min={1}
                max={maxZoom}
                step={0.1}
                value={scale}
                onChange={(value) => handleZoomChange(value)}
              />
              {typeof onRotateRight === 'function' && (
                <button
                  type="button"
                  className="gallery-rotate-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRotateRight();
                  }}
                  aria-label="Rotate image clockwise"
                >
                  <ArrowClockwise size={18} weight="bold" />
                </button>
              )}
            </div>
          )}
        </div>
        {/* Only show navigation buttons if showNavButtons is true */}
        {showNavButtons && (
          <button 
            className="gallery-nav-button next" 
            onClick={handleNext} 
            disabled={!canGoNext}
            aria-label="Next image"
          >
            <CaretRight size={32} weight="bold" />
          </button>
        )}
      </div>
      <div className="thumbnails-container">
        {images.map((image, index) => {
          const isTranscodingThumb = isTranscoding(image);
          const isFailedThumb = isFailed(image);
          const isPdfThumb = Boolean(image?.type === 'pdf' || /\.pdf(?:$|\?)/i.test(image?.url || image?.thumbnail || ''));
          const isVideoThumb = Boolean(image?.type === 'video' || /\.(mp4|mov|webm)(?:$|\?)/i.test(image?.url || image?.thumbnail || ''));
          return (
            <button
              key={image.entry_id || image.thumbnail || image.url || index}
              className={`thumbnail-button ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`View ${image.alt}`}
            >
              {isTranscodingThumb || isFailedThumb || isPdfThumb || isVideoThumb ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', height: '100%', background: 'var(--pdf-thumb-bg, #f8fafc)'
                }}>
                  {isTranscodingThumb ? <SpinnerGap size={20} className="animate-spin" />
                    : isFailedThumb ? <WarningCircle size={20} weight="duotone" />
                    : isPdfThumb ? <FilePdf size={20} weight="duotone" />
                    : <FilmSlate size={20} weight="duotone" />}
                </div>
              ) : (
                <img src={image.thumbnail} alt={image.alt} draggable={false} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['image', 'pdf', 'video']),
      media_type: PropTypes.oneOf(['image', 'video']),
      status: PropTypes.oneOf(['transcoding', 'ready', 'failed']),
      entry_id: PropTypes.string,
    })
  ).isRequired,
  initialIndex: PropTypes.number,
  maxZoom: PropTypes.number,
  onZoomedInteractionStart: PropTypes.func,
  onZoomedInteractionEnd: PropTypes.func,
  showNavButtons: PropTypes.bool,
  onIndexChange: PropTypes.func,
  onImageClick: PropTypes.func,
  renderCaption: PropTypes.func,
  rotation: PropTypes.number,
  onRotateLeft: PropTypes.func,
  onRotateRight: PropTypes.func,
};

export default ImageGallery;
